import './UnitePackageModal.css'

import React, { useCallback, useMemo, useState } from 'react'
import { Alert, Button, Modal, Spinner } from 'react-bootstrap'

import { useCreatePkgReq } from '../../../api/package-req/mutations/useCreatePkgReq'
import { DataGrid } from '../../../Components/DataGrid/DataGrid'
import calcWeight from '../../../helpers/CalculationFunction'
import { useUserColDefs } from './useUserColDefs'

export const UnitePackageModal = ({ show, handleClose, selectedPackage, allPackages, pkgReqs }) => {
  const [selectedPackages, setSelectedPackages] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const { mutateAsync: createPackageReqMutation } = useCreatePkgReq()
  // Filter packages that can be united with the selected package
  const compatiblePackages = useMemo(() => {
    if (!selectedPackage || !allPackages) return []

    return allPackages.filter(pkg => {
      // Same warehouse
      if (pkg.warehouse !== selectedPackage.warehouse) return false

      // Not the same package
      if (pkg.id === selectedPackage.id) return false
      const activePkgReqs = pkgReqs.filter(
        req =>
          (req.packageId === pkg.id || req.unitePackageIds?.includes(pkg.id)) &&
          (!req.statusUpdates || req.statusUpdates[0].status === 'pending')
      )
      const isForUnite = pkg.isForUnite || activePkgReqs?.some(req => req.unitePackageIds?.includes(pkg.id))
      const isReturnRequestActive = pkg.isReturnRequestActive || activePkgReqs?.some(req => req.reqType === 'returnRequest')
      const isShrinkRequestActive = pkg.isShrinkRequestActive || activePkgReqs?.some(req => req.reqType === 'shrinkRequest')
      const isPhotoRequestActive = pkg.isPhotoRequestActive || activePkgReqs?.some(req => req.reqType === 'photoRequest')
      // Can be united (same logic as getCanUnite)
      if (
        pkg.isPaid ||
        pkg.isShipped ||
        pkg.isDraft ||
        isForUnite ||
        isReturnRequestActive ||
        isShrinkRequestActive ||
        isPhotoRequestActive
      ) {
        return false
      }

      return true
    })
  }, [selectedPackage, allPackages, pkgReqs])

  // Check if packages can be united (weight and flammable checks)
  const canUnitePackages = useCallback(packages => {
    if (packages.length < 2) return { canUnite: false, reason: 'Need at least 2 packages' }

    // Weight check
    let totalWeight = 0
    let hasFlammable = false
    let hasNormal = false

    packages.forEach(pkg => {
      totalWeight += calcWeight(pkg.width, pkg.length, pkg.height, pkg.weight)
      if (pkg.isFlammable) hasFlammable = true
      else hasNormal = true
    })

    if (totalWeight >= 20) {
      return { canUnite: false, reason: 'Total weight exceeds 20kg limit' }
    }

    if (hasFlammable && hasNormal) {
      return { canUnite: false, reason: 'Cannot unite flammable and normal packages' }
    }

    return { canUnite: true }
  }, [])

  const handleRowSelection = useCallback(event => {
    const selectedNodes = event.api.getSelectedNodes()
    const selectedIds = selectedNodes.map(node => node.data.id)
    setSelectedPackages(selectedIds)
    setError('')
  }, [])

  const handleUnite = async () => {
    if (selectedPackages.length < 1) {
      setError('Please select at least one package to unite with')
      return
    }

    const packagesToUnite = [selectedPackage, ...compatiblePackages.filter(p => selectedPackages.includes(p.id))]
    const uniteCheck = canUnitePackages(packagesToUnite)

    if (!uniteCheck.canUnite) {
      setError(uniteCheck.reason)
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const uniteResult = await createPackageReqMutation({
        reqType: 'uniteRequest',
        unitePackageIds: packagesToUnite.map(pkg => pkg.id),
        // Add other necessary fields for unite requests
      })
      if (uniteResult.statusCode !== 200) {
        setError(uniteResult.msg || 'Error creating unite package. Please try again.')
        return
      }
      handleClose()
    } catch (error) {
      console.error('Error creating unite package:', error)
      setError('Error creating unite package. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }
  const isPackageSubtracted = useCallback(pkg => {
    if (pkg.delayStatus == '30' || pkg.delayStatus == 'SUBTRACTED' || pkg.delayStatus == 'DONATED') return true
    else return false
  }, [])

  const getStatus = useCallback(
    pkg => {
      if (pkg.isShipped) return 'Shipped'
      else if (isPackageSubtracted(pkg)) return 'Subtracted'
      else return pkg.isDraft ? 'At the Warehouse - DRAFT' : 'At the Warehouse'
    },
    [isPackageSubtracted]
  )
  const columnDefs = useUserColDefs({
    getStatus,
  })

  return (
    <Modal show={show} onHide={handleClose} size='xl' centered className='unite-package-modal'>
      <Modal.Header closeButton>
        <Modal.Title>Unite Packages</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='selected-package-info'>
          <h6>Selected Package:</h6>
          <div>
            <strong>{selectedPackage?.brand}</strong> - {selectedPackage?.trackingNumberToIsrael}({selectedPackage?.warehouse}) - $
            {selectedPackage?.valueInUSD || 0}
          </div>
        </div>

        <div className='compatible-packages-header'>
          <h6>Compatible Packages ({compatiblePackages.length}):</h6>
          <p className='compatible-packages-description'>
            Packages that can be united with the selected package (same customer, warehouse, and not in process)
          </p>
        </div>

        {error && (
          <Alert variant='danger' className='mb-3'>
            {error}
          </Alert>
        )}

        <div style={{ height: '400px' }}>
          <DataGrid
            hideHeader
            rowData={compatiblePackages}
            columnDefs={columnDefs}
            onSelectionChanged={handleRowSelection}
            rowSelection={{
              mode: 'multiRow',
              enableClickSelection: false,
            }}
            entityName='unitePackages'
            searchPlaceholder='Search packages...'
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleUnite} disabled={isLoading || selectedPackages.length === 0}>
          {isLoading ? (
            <>
              <Spinner animation='border' size='sm' className='me-2' />
              Creating Unite...
            </>
          ) : (
            `Unite Packages (${selectedPackages.length + 1})`
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default UnitePackageModal
